import { FOOTER_ROW_STYLES, FILTER_KEYS } from '../consts';
import useFilters from '../hooks/useFilters';
import { useQuery } from '@tanstack/react-query';
import { getOrders } from 'api/requests/giftCard/giftCard';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_NUM } from 'shared/consts/giftCards';
import useFilterSelectors from 'shared/hooks/useFilterSelectors';
import { queryKeyBuilder } from 'shared/query';
import { isDefined } from 'shared/utils/bids';

function OrdersFooter() {
  const urlQueryParams = useFilters();
  const { onSingleFilterSelected } = useFilterSelectors();

  const { data: ordersData, isPending } = useQuery({
    queryKey: queryKeyBuilder.giftCardsToShow(urlQueryParams),
    queryFn: () => getOrders(urlQueryParams),
  });

  const currentPage = Number.parseInt(
    isDefined(urlQueryParams.pageNum) ? (urlQueryParams.pageNum as string) : String(DEFAULT_PAGE_NUM)
  );
  const totalOrders = ordersData?.total ?? 0;
  const totalPages = Math.ceil(totalOrders / DEFAULT_PAGE_SIZE);

  const getPageRange = () => {
    const start = Math.max(currentPage - 2, 1);
    const end = Math.min(currentPage + 2, totalPages);
    const pagesToShow = [];
    const separator = ['.', '.', '.'];

    if (start > 1) {
      pagesToShow.push(1);
    }
    if (start > 2) {
      pagesToShow.push(...separator);
    }
    for (let i = start; i <= end; i++) {
      pagesToShow.push(i);
    }
    if (end < totalPages && end !== start - 1) {
      pagesToShow.push(...separator);
    }
    if (end < totalPages) {
      pagesToShow.push(totalPages);
    }
    return pagesToShow;
  };

  const displayRangeLabel = `${
    (+(urlQueryParams?.pageNum ?? DEFAULT_PAGE_NUM) - 1) * DEFAULT_PAGE_SIZE + 1
  } - ${Math.min(+(urlQueryParams?.pageNum ?? DEFAULT_PAGE_NUM) * DEFAULT_PAGE_SIZE, totalOrders)}`;

  return isPending ? null : (
    <div className={FOOTER_ROW_STYLES}>
      <span className="font-inter font-normal text-sm text-palette-secondary-grey">
        Showing {displayRangeLabel} of {totalOrders} results
      </span>
      <div className="pagination flex justify-between text-sm gap-x-12">
        <button
          onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(currentPage - 1))}
          disabled={currentPage === 1}
          className="text-palette-secondary uppercase before:content-['\<'] disabled:text-palette-light-grey"
        >
          <span className="underline">Previous</span>
        </button>
        <div className="flex justify-between gap-x-1.5">
          {getPageRange().map((page, index) => {
            if (typeof page === 'number') {
              return (
                <button
                  key={index}
                  onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(page))}
                  disabled={page === currentPage}
                  className={
                    page === currentPage
                      ? 'text-palette-burgundy bg-palette-burgundy/10 rounded-full py-1 px-3'
                      : 'hover:text-palette-burgundy hover:bg-palette-burgundy/10 rounded-full py-1 px-3'
                  }
                >
                  {page}
                </button>
              );
            }
            return (
              <span className="py-1 px-1" key={index}>
                {page}
              </span>
            );
          })}
        </div>
        <button
          onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(currentPage + 1))}
          disabled={currentPage === totalPages}
          className="text-palette-secondary uppercase after:content-['\>'] disabled:text-palette-light-grey"
        >
          <span className="underline">Next</span>
        </button>
      </div>
    </div>
  );
}

export default OrdersFooter;
